import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import * as Mutation from '../../../api/gql/mutation'
import { linkEndpointLabel } from '../lib/link-labels'
import type { Tree } from '../lib/tree'
import { useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity, ScheduleDependency, ScheduleExternalActivity } from '../types'
import type { RunFn } from '../detail/gantt-ops'

interface Deps {
  scheduleId: string
  selectedLinkId: string | null
  dependencies: ScheduleDependency[]
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  tree: Tree<ScheduleActivity>
  /** The single mutation queue for the whole detail page (see `GanttView`). */
  run: RunFn
}

/**
 * The timeline's link-delete confirmation (spec B §8.5, Task 18): state and handlers for the
 * `Delete`-on-a-selected-link flow wired through `useGanttKeyboard`'s `onRowOp`. Extracted out of
 * `GanttView` in fix round 1 (review finding: file crossed 500 lines) — a pure move alongside the
 * existing `useRowOps` extraction, no behaviour change. Endpoint labels reuse `linkEndpointLabel`,
 * the same helper `DependenciesSection` uses, so both describe an endpoint identically.
 */
export const useLinkDelete = ({ scheduleId, selectedLinkId, dependencies, byId, externalById, tree, run }: Deps) => {
  const api = useGanttStoreApi()
  const [deleteDependency] = useMutation(Mutation.DELETE_SCHEDULE_DEPENDENCY)
  const [linkDeleteTarget, setLinkDeleteTarget] = useState<{ id: string; from: string; to: string } | null>(null)
  const [linkDeleting, setLinkDeleting] = useState(false)

  const askDeleteLink = useCallback(() => {
    if (!selectedLinkId) return
    const dep = dependencies.find((d) => d.id === selectedLinkId)
    if (!dep) return
    const from = linkEndpointLabel(byId, externalById, tree.rowNumberById, dep.predecessorId).label
    const to = linkEndpointLabel(byId, externalById, tree.rowNumberById, dep.successorId).label
    setLinkDeleteTarget({ id: selectedLinkId, from, to })
  }, [selectedLinkId, dependencies, byId, externalById, tree])

  const cancelDeleteLink = useCallback(() => setLinkDeleteTarget(null), [])

  const confirmDeleteLink = useCallback(async () => {
    if (!linkDeleteTarget) return
    setLinkDeleting(true)
    try {
      await run(
        () => deleteDependency({ variables: { scheduleId, id: linkDeleteTarget.id } }) as unknown as Promise<{ data: any }>,
        (d: any) => d.deleteScheduleDependency,
      )
      api.getState().setSelectedLink(null)
      setLinkDeleteTarget(null)
    } catch { /* run() already toasted */ } finally { setLinkDeleting(false) }
  }, [linkDeleteTarget, run, deleteDependency, scheduleId, api])

  return { linkDeleteTarget, linkDeleting, askDeleteLink, cancelDeleteLink, confirmDeleteLink }
}
